import { css } from "@emotion/react";
import { useForm } from "@tanstack/react-form";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
//import { SiteLayout } from "./SiteLayout";
import type {
	RegisterFormState /* useRegisterFormState*/,
} from "../hooks/useRegisterFormState";
import { useStorefrontSettingsState } from "../hooks/useStorefrontSettingsState";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import FwPhoneNumberBox from "../lib/FwReact/FwPhoneNumberBox";

type RegisterFormProps = {
	registerFormState: RegisterFormState;
	onRegistered?: () => void;
};
export const RegisterForm: React.FC<RegisterFormProps> = (props) => {
	//const theme = useGlobalTheme();
	const fieldSpacing: string = "0.75rem";
	const isFormDisabled = props.registerFormState.get().isWaitingToRegister;
	const storefrontSettingsState = useStorefrontSettingsState();
	const isUniversity =
		storefrontSettingsState.get().settings?.IsUniversity || false;
	const studentIdRequired =
		isUniversity &&
		(storefrontSettingsState.get().settings
			?.StudentIdRequiredOnRegistrationForm ||
			false);
	const phoneNoRequired =
		storefrontSettingsState.get().settings?.PhoneNoRequiredOnRegistrationForm ||
		false;
	const translate = useTranslate();
	const registerForm = useForm({
		defaultValues: {
			firstName: props.registerFormState.get().FirstName,
			lastName: props.registerFormState.get().LastName,
			phone: props.registerFormState.get().Phone ?? "",
			email: props.registerFormState.get().Email,
			studentId: props.registerFormState.get().StudentId,
		},
		onSubmit: async ({ value }) => {
			props.registerFormState.setFirstName(value.firstName);
			props.registerFormState.setLastName(value.lastName);
			props.registerFormState.setPhone(value.phone);
			props.registerFormState.setEmail(value.email);
			props.registerFormState.setStudentId(value.studentId);
			const isRegistered = await props.registerFormState.registerAsync();
			if (isRegistered) {
				props.onRegistered?.();
			}
		},
	});
	const getRegisterFieldError = (errors: readonly unknown[]) => {
		return typeof errors[0] === "string" ? errors[0] : "";
	};

	return (
		<Card className="mx-auto w-80 sm:w-90">
			<CardHeader>
				<CardTitle>{translate("Register")}</CardTitle>
				<CardDescription>
					{translate("Fill out the fields to signup for a new account")}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<form
					noValidate
					autoComplete="off"
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						void registerForm.handleSubmit();
					}}
				>
					<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
						<div css={css`width:100%;`}>
							<registerForm.Field
								name="firstName"
								validators={{
									onSubmit: ({ value }) =>
										value.trim().length === 0
											? translate("First Name is required")
											: undefined,
								}}
							>
								{(field) => {
									const showError =
										field.state.meta.isTouched ||
										registerForm.state.submissionAttempts > 0;
									const error = getRegisterFieldError(field.state.meta.errors);
									return (
										<Field>
											<FieldLabel
												className="mb-1 inline-block"
												htmlFor="registerFirstName"
											>
												{translate("First Name")}{" "}
												<span className="text-destructive">*</span>
											</FieldLabel>
											{showError && error.length > 0 && (
												<FieldError>{error}</FieldError>
											)}
											<Input
												id="registerFirstName"
												placeholder={translate("First Name")}
												type="text"
												required
												value={field.state.value}
												maxLength={50}
												onChange={(e) => {
													const value = e.currentTarget.value;
													field.handleChange(value);
													props.registerFormState.setFirstName(value);
												}}
												onBlur={field.handleBlur}
												aria-invalid={showError && error.length > 0}
											/>
										</Field>
									);
								}}
							</registerForm.Field>
						</div>
					</div>
					<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
						<div css={css`width:100%;`}>
							<registerForm.Field
								name="lastName"
								validators={{
									onSubmit: ({ value }) =>
										value.trim().length === 0
											? translate("Last Name is required")
											: undefined,
								}}
							>
								{(field) => {
									const showError =
										field.state.meta.isTouched ||
										registerForm.state.submissionAttempts > 0;
									const error = getRegisterFieldError(field.state.meta.errors);
									return (
										<Field>
											<FieldLabel
												className="mb-1 inline-block"
												htmlFor="registerLastName"
											>
												{translate("Last Name")}{" "}
												<span className="text-destructive">*</span>
											</FieldLabel>
											{showError && error.length > 0 && (
												<FieldError>{error}</FieldError>
											)}
											<Input
												id="registerLastName"
												placeholder={translate("Last Name")}
												type="text"
												required
												value={field.state.value}
												maxLength={50}
												onChange={(e) => {
													const value = e.currentTarget.value;
													field.handleChange(value);
													props.registerFormState.setLastName(value);
												}}
												onBlur={field.handleBlur}
												aria-invalid={showError && error.length > 0}
											/>
										</Field>
									);
								}}
							</registerForm.Field>
						</div>
					</div>
					<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
						<registerForm.Field
							name="phone"
							validators={{
								onSubmit: ({ value }) =>
									phoneNoRequired && value.trim().length === 0
										? translate("Phone is required")
										: undefined,
							}}
						>
							{(field) => {
								const showError =
									field.state.meta.isTouched ||
									registerForm.state.submissionAttempts > 0;
								const error = getRegisterFieldError(field.state.meta.errors);
								return (
									<Field>
										{showError && error.length > 0 && (
											<FieldError>{error}</FieldError>
										)}
										<div onBlurCapture={field.handleBlur}>
											<FwPhoneNumberBox
												label={translate("Phone")}
												required={phoneNoRequired}
												value={field.state.value}
												onChange={(e) => {
													const value = e.currentTarget.value;
													field.handleChange(value);
													props.registerFormState.setPhone(value);
												}}
											/>
										</div>
									</Field>
								);
							}}
						</registerForm.Field>
					</div>
					<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
						<div css={css`width:100%;`}>
							<registerForm.Field
								name="email"
								validators={{
									onSubmit: ({ value }) => {
										const email = value.trim();
										if (email.length === 0) {
											return translate("Email is required");
										}
										if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
											return translate("Enter a valid email address");
										}
										return undefined;
									},
								}}
							>
								{(field) => {
									const showError =
										field.state.meta.isTouched ||
										registerForm.state.submissionAttempts > 0;
									const error = getRegisterFieldError(field.state.meta.errors);
									return (
										<Field>
											<FieldLabel
												className="mb-1 inline-block"
												htmlFor="registerEmail"
											>
												{translate("Email")}{" "}
												<span className="text-destructive">*</span>
											</FieldLabel>
											{showError && error.length > 0 && (
												<FieldError>{error}</FieldError>
											)}
											<Input
												id="registerEmail"
												placeholder={translate("Email")}
												type="email"
												required
												value={field.state.value}
												maxLength={255}
												onChange={(e) => {
													const value = e.currentTarget.value;
													field.handleChange(value);
													props.registerFormState.setEmail(value);
												}}
												onBlur={field.handleBlur}
												aria-invalid={showError && error.length > 0}
											/>
										</Field>
									);
								}}
							</registerForm.Field>
						</div>
					</div>
					{isUniversity && (
						<div css={css`display:flex;margin-bottom:${fieldSpacing};`}>
							<div css={css`width:100%;`}>
								<registerForm.Field
									name="studentId"
									validators={{
										onSubmit: ({ value }) =>
											studentIdRequired && value.trim().length === 0
												? translate("Student ID is required")
												: undefined,
									}}
								>
									{(field) => {
										const showError =
											field.state.meta.isTouched ||
											registerForm.state.submissionAttempts > 0;
										const error = getRegisterFieldError(
											field.state.meta.errors,
										);
										return (
											<Field>
												<FieldLabel
													className="mb-1 inline-block"
													htmlFor="registerStudentId"
												>
													{translate("Student ID")}
													{studentIdRequired && (
														<>
															{" "}
															<span className="text-destructive">*</span>
														</>
													)}
												</FieldLabel>
												{showError && error.length > 0 && (
													<FieldError>{error}</FieldError>
												)}
												<Input
													id="registerStudentId"
													placeholder={translate("Student ID")}
													type="text"
													required={studentIdRequired}
													value={field.state.value}
													maxLength={255}
													onChange={(e) => {
														const value = e.currentTarget.value;
														field.handleChange(value);
														props.registerFormState.setStudentId(value);
													}}
													onBlur={field.handleBlur}
													aria-invalid={showError && error.length > 0}
												/>
											</Field>
										);
									}}
								</registerForm.Field>
							</div>
						</div>
					)}
					<div
						css={css`
                    display:flex;
                    margin-bottom:${fieldSpacing};
                    align-items:center;
                    justify-content:center;`}
					>
						<Button
							type="submit"
							variant="default"
							className="w-full"
							disabled={isFormDisabled || registerForm.state.isSubmitting}
						>
							{translate("Register")}
							<UserPlus css={css`margin-left: 4px;`} />
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
};

//type RegisterPageProps = {

//}
//export const ContactRegistrationPage: React.FC<RegisterPageProps> = props => {
//    //const theme = useGlobalTheme();
//    const contactRegistrationController = useRegisterFormState();

//    return (<SiteLayout
//        title={contactRegistrationController.translate('User Registration')}
//        mainChildren={
//            <div css={css`
//                min-width:350px;
//                max-width:960px;
//                margin:0 auto;`}>
//                <RegisterForm registerFormState={contactRegistrationController} />
//            </div>
//        }
//    />);
//}

//export default SignUpPage
